// Reading a diary again for a week that is already saved. Rows get new ids each time they are
// read, so without this the actions, notes and fees entered on the week would be lost. They are
// carried onto the new rows by date and client.
//
// If any row's work has nowhere to go, the caller keeps the earlier saved week as well, so that
// reading a diary again can never lose work.

import { isDiaryHeadingRow } from "./diaryHeadingRows.js";

const letters = (value = "") => String(value).toLowerCase().replace(/[^a-z]/g, "");
const surname = (value = "") => letters(String(value).split(",")[0]);
const rowKey = (entry) => `${entry.date}|${letters(entry.client_name)}`;

const hasContent = (value) => Boolean(value) && (typeof value !== "object" || Object.keys(value).length > 0);

function workOn(id, data) {
  return Boolean(data.actions?.[id] || data.notes?.[id] || hasContent(data.fees?.[id]) || hasContent(data.aid?.[id]));
}

// The saved week to carry from: the latest with this name that has any work on it.
// `drafts` is newest first.
export function findWeekToCarryOver(drafts = [], weekName = "") {
  return drafts.find((draft) => {
    const data = draft?.form_data;
    return draft?.draft_name === weekName
      && (data?.result?.entries || []).some((entry) => workOn(entry.id, data));
  }) || null;
}

// newEntries: the rows just read, with their new ids. data: the saved week's form_data.
// Returns the week's work keyed by the new ids; `pairs` is [oldEntry, newEntry] for each row
// matched, and `unmatched` the earlier rows with work that found no new row.
export function carryOverWeek(newEntries = [], data = {}) {
  const oldEntries = (data.result?.entries || []).filter((entry) => !isDiaryHeadingRow(entry));
  const free = new Set(newEntries);
  const pairs = [];
  const unplaced = [];

  // Same date and full name, in diary order where a client appears twice in a day.
  for (const old of oldEntries) {
    const match = newEntries.find((entry) => free.has(entry) && rowKey(entry) === rowKey(old));
    if (match) {
      free.delete(match);
      pairs.push([old, match]);
    } else {
      unplaced.push(old);
    }
  }

  // Then same date and surname, if that leaves only one candidate: a name misread by a scan.
  const unmatched = [];
  for (const old of unplaced) {
    const candidates = [...free].filter((entry) => entry.date === old.date && surname(entry.client_name) === surname(old.client_name));
    if (surname(old.client_name) && candidates.length === 1) {
      free.delete(candidates[0]);
      pairs.push([old, candidates[0]]);
    } else if (workOn(old.id, data)) {
      unmatched.push(old);
    }
  }

  const carried = { actions: {}, notes: {}, fees: {}, aid: {} };
  let kept = 0;
  for (const [old, entry] of pairs) {
    if (workOn(old.id, data)) kept += 1;
    for (const field of Object.keys(carried)) {
      const value = data[field]?.[old.id];
      if (hasContent(value)) carried[field][entry.id] = value;
    }
  }

  return { ...carried, pairs, kept, unmatched };
}

// Held-list items (Aid Pending and so on) are keyed partly by court, which reading again can
// correct. Moves each item to its row's new key and details rather than listing it twice.
export function rekeyHeldItems(items = [], pairs = [], keyOf) {
  const byOldKey = new Map(pairs.map(([old, entry]) => [keyOf(old), entry]));
  return items.map((item) => {
    const entry = byOldKey.get(item.pendingKey || keyOf(item));
    if (!entry) return item;
    return {
      ...item,
      id: entry.id,
      court: entry.court,
      appearance_type: entry.appearance_type,
      lawyer_initials: entry.lawyer_initials,
      outcome: entry.outcome,
      pendingKey: keyOf(entry),
    };
  });
}
